import { getChunkedUploadStatus } from "@/js/upload/api";
import { subscribeSseEvents, supportsSse } from "@/js/upload/sse";

// Upload status helpers: normalize, poll, and SSE-based completion tracking.
const extractProcessingStatus = status => {
  if (!status) {
    return null;
  }
  const totalFiles = Number(status.total_files) || 0;
  const processedFiles = Number(status.processed_files) || 0;
  return {
    status: status.status,
    total_files: totalFiles,
    processed_files: processedFiles
  };
};

const normalizeUploadStatus = value => {
  if (!value) {
    return "";
  }
  return String(value).toLowerCase();
};

// Normalize processing status with series totals + cumulative offsets.
const buildProcessingPayload = (status, context = {}) => {
  const processing = extractProcessingStatus(status);
  if (!processing) {
    return null;
  }
  const seriesTotal = Number(context.seriesTotalFiles) || 0;
  const processedOffset = Number(context.processedOffset) || 0;
  const seriesTotalFiles =
    seriesTotal > 0 ? seriesTotal : processing.total_files;
  const seriesProcessedFiles = processedOffset + processing.processed_files;
  const seriesComplete =
    seriesTotalFiles > 0 && seriesProcessedFiles >= seriesTotalFiles;
  // Avoid marking a series as completed when only a single batch is done.
  const normalizedStatus =
    normalizeUploadStatus(processing.status) === "completed" && !seriesComplete
      ? "ingesting"
      : normalizeUploadStatus(processing.status);
  return {
    ...processing,
    status: normalizedStatus,
    processed_offset: processedOffset,
    series_total_files: seriesTotalFiles,
    series_processed_files: seriesProcessedFiles,
    series_complete: seriesComplete
  };
};

// Emit a synthetic processing snapshot so UI stays in sync between batches.
const emitProcessingSnapshot = (sub, processedBase, seriesTotalFiles) => {
  if (!sub || !Number.isFinite(seriesTotalFiles) || seriesTotalFiles <= 0) {
    return;
  }
  const statusValue =
    processedBase >= seriesTotalFiles ? "completed" : "ingesting";
  sub.next({
    processing: {
      status: statusValue,
      processed_files: processedBase,
      total_files: seriesTotalFiles,
      processed_offset: 0,
      series_total_files: seriesTotalFiles
    }
  });
};

const notifyProcessingStatus = async (uploadId, onStatus) => {
  if (typeof onStatus !== "function") {
    return null;
  }
  try {
    // Poll without received_chunks to keep payloads small during long uploads.
    const status = await getChunkedUploadStatus(uploadId, {
      includeChunks: false
    });
    onStatus(status);
    return status;
  } catch (error) {
    return null;
  }
};

const pollUploadStatus = async (uploadId, options = {}) => {
  const retryFinalize =
    typeof options.retryFinalize === "function" ? options.retryFinalize : null;
  const onStatus = typeof options.onStatus === "function" ? options.onStatus : null;
  let retryEvery = Number.isFinite(options.retryEvery) ? options.retryEvery : 5;

  // Poll until the Celery ingest task completes (or fails).
  // Without this, the frontend considers the upload done before the Image
  // record exists in the DB, causing "no images found" in the viewer.
  let pollInterval = Number.isFinite(options.pollIntervalMs)
    ? options.pollIntervalMs
    : 1000;
  let maxPollInterval = pollInterval;
  let backoffMultiplier = 1;
  const MAX_POLLS = 300; // ~5 minutes at 1s base, longer when backoff is active.
  let statusErrors = 0;
  const MAX_STATUS_ERRORS = 5;
  let throttled = options.throttled === true;

  // Increase poll interval + reduce retry cadence after ingest throttling (429).
  const configureBackoff = () => {
    if (!Number.isFinite(options.retryEvery)) {
      retryEvery = 12;
    }
    pollInterval = Number.isFinite(options.backoffInitialMs)
      ? options.backoffInitialMs
      : 5000;
    maxPollInterval = Number.isFinite(options.backoffMaxMs)
      ? options.backoffMaxMs
      : 20000;
    backoffMultiplier = Number.isFinite(options.backoffMultiplier)
      ? options.backoffMultiplier
      : 2;
  };

  const enableBackoff = () => {
    if (throttled) {
      return;
    }
    throttled = true;
    configureBackoff();
  };

  if (throttled) {
    configureBackoff();
  }

  for (let i = 0; i < MAX_POLLS; i++) {
    await new Promise(r => setTimeout(r, pollInterval));
    try {
      // Poll without received_chunks to keep payloads small during long uploads.
      const pollStatus = await getChunkedUploadStatus(uploadId, {
        includeChunks: false
      });
      statusErrors = 0;
      if (!pollStatus) {
        continue;
      }
      if (onStatus) {
        onStatus(pollStatus);
      }
      const normalizedStatus = normalizeUploadStatus(pollStatus.status);
      if (normalizedStatus === "completed") {
        return;
      }
      if (normalizedStatus === "failed") {
        throw new Error(
          pollStatus.error_message || "Server-side ingest failed"
        );
      }
      if (normalizedStatus === "cancelled") {
        throw new Error("Upload cancelled");
      }
      if (
        normalizedStatus === "uploaded" &&
        retryFinalize &&
        i % retryEvery === 0
      ) {
        const finalizeResult = await retryFinalize();
        if (finalizeResult && finalizeResult._throttled) {
          enableBackoff();
        }
      }
    } catch (error) {
      statusErrors += 1;
      if (statusErrors >= MAX_STATUS_ERRORS) {
        throw error;
      }
    }

    if (throttled && pollInterval < maxPollInterval) {
      pollInterval = Math.min(
        maxPollInterval,
        pollInterval * backoffMultiplier
      );
    }
  }
};

const waitForUploadCompletion = async (uploadId, options = {}) => {
  const retryFinalize =
    typeof options.retryFinalize === "function" ? options.retryFinalize : null;
  const onStatus = typeof options.onStatus === "function" ? options.onStatus : null;
  const useSse = options.useSse !== false;

  if (!useSse || !supportsSse()) {
    return pollUploadStatus(uploadId, options);
  }

  return new Promise((resolve, reject) => {
    let finalizedOnce = false;
    let fallbackStarted = false;

    const startPollingFallback = throttled => {
      if (fallbackStarted) {
        return;
      }
      fallbackStarted = true;
      pollUploadStatus(uploadId, { ...options, throttled })
        .then(resolve)
        .catch(reject);
    };

    const closeAndResolve = () => {
      if (subscription) {
        subscription.close();
      }
      resolve();
    };

    const closeAndReject = error => {
      if (subscription) {
        subscription.close();
      }
      reject(error);
    };

    const handleStatus = status => {
      if (!status) {
        return;
      }
      if (onStatus) {
        onStatus(status);
      }
      const state = normalizeUploadStatus(status.status);
      if (state === "completed") {
        closeAndResolve();
        return;
      }
      if (state === "failed") {
        closeAndReject(
          new Error(status.error_message || "Server-side ingest failed")
        );
        return;
      }
      if (state === "cancelled") {
        closeAndReject(new Error("Upload cancelled"));
        return;
      }
      if (state === "uploaded" && retryFinalize && !finalizedOnce) {
        finalizedOnce = true;
        retryFinalize()
          .then(result => {
            if (result && result._throttled) {
              if (subscription) {
                subscription.close();
              }
              startPollingFallback(true);
            }
          })
          .catch(error => {
            closeAndReject(error);
          });
      }
    };

    let subscription = null;
    subscription = subscribeSseEvents(`/api/uploads/${uploadId}/events/`, {
      events: {
        upload_status: event => {
          try {
            handleStatus(JSON.parse(event.data));
          } catch (error) {
            if (subscription) {
              subscription.close();
            }
            startPollingFallback(options.throttled);
          }
        }
      },
      onError: () => {
        if (subscription) {
          subscription.close();
        }
        startPollingFallback(options.throttled);
      }
    });

    if (!subscription) {
      startPollingFallback(options.throttled);
    }
  });
};

export {
  buildProcessingPayload,
  emitProcessingSnapshot,
  extractProcessingStatus,
  normalizeUploadStatus,
  notifyProcessingStatus,
  pollUploadStatus,
  waitForUploadCompletion
};
